
import React, { useContext } from 'react';
import { SettingsSearchContext, ConfigContext } from './SettingsContext';
import { InfoPopup } from './InfoPopup';
import { SETTING_INFO } from '@/data/settingInfo';
import { STUDIO_THEME } from './StudioTheme';

interface ControlGroupProps {
  label: string;
  children?: React.ReactNode;
  value?: string | number;
  tooltip?: string;
  settingKey?: string;
  disabled?: boolean;
  disabledMessage?: string;
  modes?: string[];
  className?: string;
  darkMode?: boolean;
}

export const ControlGroup: React.FC<ControlGroupProps> = ({ 
  label, children, value, tooltip, settingKey, disabled, disabledMessage, modes, className, darkMode 
}) => {
  const searchQuery = useContext(SettingsSearchContext);
  const config = useContext(ConfigContext);

  // Automatically fetch info from SETTING_INFO if settingKey is provided
  const info = settingKey ? SETTING_INFO[settingKey] : null;
  const finalTooltip = tooltip || info?.description || '';
  const finalModes = modes || info?.modes;

  // Governance Logic
  let isApplicable = true;
  if (config && finalModes && finalModes.length > 0) {
      const mode = (config as any).mode;
      const isGlobal = finalModes.includes('GLOBAL');
      const matchesCurrentMode = finalModes.includes(mode);
      
      // Feature Flag Check
      const hasRhythmic = finalModes.includes('RHYTHMIC') && (config as any).enableRhythmic;
      const hasEscape = finalModes.includes('ESCAPE') && (config as any).enableEscape;
      const hasTrails = finalModes.includes('TRAILS') && ((config as any).enableTrails || (config as any).trailEnabled);
      const hasSpawner = finalModes.includes('SPAWNER') && (config as any).enableSpawner;
      const hasDrawer = finalModes.includes('DRAWER') && (config as any).enableDrawer;
      const hasSpaceSpiral = finalModes.includes('SPACE_SPIRAL') && (config as any).enableSpaceSpiral;

      isApplicable = isGlobal || matchesCurrentMode || hasRhythmic || hasEscape || hasTrails || hasSpawner || hasDrawer || hasSpaceSpiral;
  }

  // If searching and label doesn't match, or if not applicable, hide.
  if (searchQuery && !label.toLowerCase().includes(searchQuery.toLowerCase())) {
    return null;
  }
  
  if (!isApplicable && !searchQuery) {
     return null; // Hide non-applicable settings unless searching
  }

  const finalDisabled = disabled || !isApplicable;
  const finalDisabledMessage = disabledMessage || (isApplicable ? '' : 'This setting is not compatible with the current active mode or features.');

  const hudKey = settingKey ? `hudShow${settingKey.charAt(0).toUpperCase()}${settingKey.slice(1)}` : null;
  const isHudEnabled = hudKey && config ? (config as any)[hudKey] : false;

  // Detect if children is just a Toggle for inline layout
  const isToggle = React.Children.count(children) === 1 && 
                   React.isValidElement(children) && 
                   ((children.type as any).name === 'Toggle' || (children.props as any).active !== undefined);

  const toggleHud = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hudKey && (window as any).setConfig) {
        (window as any).setConfig((prev: any) => ({ ...prev, [hudKey]: !prev[hudKey] }));
    }
  };

  return (
    <div className={`flex ${isToggle ? 'flex-row items-center justify-between' : 'flex-col items-start gap-2.5'} w-full py-2.5 transition-all duration-300 min-w-0 ${finalDisabled ? 'opacity-40 pointer-events-none grayscale-[0.3] blur-[0.4px]' : ''} ${className || ''}`}>
      <div className="flex flex-col gap-1 min-w-0 flex-1 overflow-hidden">
        <div className="flex items-center gap-2 w-full min-w-0">
          <label className={`${STUDIO_THEME.typography.controlLabel} opacity-70`}>{label}</label>
        </div>
        {value !== undefined && <span className={`${STUDIO_THEME.typography.subtext} ${config?.darkMode ? 'text-white/20' : 'text-zinc-950/30'}`}>{value}</span>}
      </div>

      <div className={`flex items-center gap-2 ${isToggle ? 'shrink-0' : 'w-full'}`}>
        <div className={isToggle ? '' : 'w-full'}>
            {children}
        </div>
        {(finalTooltip || (finalDisabled && finalDisabledMessage)) && (
            <InfoPopup 
                content={finalTooltip} 
                disabled={finalDisabled} 
                disabledMessage={finalDisabledMessage} 
                modes={finalModes}
                darkMode={darkMode}
            />
        )}
      </div>
    </div>
  );
};
